import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload,
  Download,
  FileSpreadsheet,
  Check,
  AlertCircle,
  Star
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ImportExport = () => {
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  const handleImport = async (type, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setImporting(true);
    setImportResults(null);

    try {
      const response = await axios.post(`${API}/import/${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResults({ 
        success: true, 
        message: response.data.message,
        details: response.data
      });
      toast.success(response.data.message);
    } catch (error) {
      setImportResults({ success: false, message: error.response?.data?.detail || 'Import failed' });
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async (type, filename) => {
    try {
      const response = await axios.get(`${API}/export/${type}`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `${type}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${filename || type}.csv downloaded`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const downloadTemplate = async (type) => {
    try {
      const response = await axios.get(`${API}/export/templates/${type}`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_template.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const downloadExcelTemplate = async () => {
    try {
      const response = await axios.get(`${API}/export/templates/recipes-excel`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'recipes_template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Excel Multi-Sheet Import Card (Featured)
  const ExcelRecipeImportCard = () => {
    const [file, setFile] = useState(null);

    return (
      <div className="virsa-card p-6 border-2 border-[#faa023] bg-gradient-to-br from-white to-[#faa023]/5">
        <div className="flex items-center gap-2 mb-2">
          <Star size={20} className="text-[#faa023]" fill="#faa023" />
          <h3 className="text-lg font-bold text-stone-800">Excel Multi-Recipe Import</h3>
          <span className="badge" style={{ backgroundColor: '#faa023', color: 'white' }}>Best for Bulk</span>
        </div>
        <p className="text-sm text-stone-500 mb-4">
          Import multiple recipes at once - each sheet in Excel = one recipe
        </p>

        <div className="bg-[#faa023]/10 rounded-lg p-3 mb-4 border border-[#faa023]/30">
          <p className="text-xs text-stone-700 space-y-1">
            <strong>Format:</strong>
            <br />• <strong>Sheet name</strong> = Recipe name
            <br />• <strong>Row 1:</strong> Category, Visibility, Cooking Steps, Important Notes
            <br />• <strong>Row 2:</strong> Values for above
            <br />• <strong>Row 4+:</strong> ingredient_name, quantity, unit
          </p>
        </div>

        <div className="space-y-3">
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="virsa-input"
            data-testid="import-recipes-excel-file"
          />
          <div className="flex gap-2">
            <button
              onClick={() => handleImport('recipes-excel', file)}
              disabled={!file || importing}
              className="flex-1 btn-primary"
              style={{ backgroundColor: '#faa023', borderColor: '#faa023' }}
              data-testid="import-recipes-excel-btn"
            >
              {importing ? <span className="spinner" /> : <Upload size={18} />}
              Import Excel
            </button>
            <button
              onClick={downloadExcelTemplate}
              className="btn-outline"
              title="Download Excel Template"
            >
              <Download size={18} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Simple Recipe Import Card (Featured)
  const SimpleRecipeImportCard = () => {
    const [file, setFile] = useState(null);

    return (
      <div className="virsa-card p-6 border-2 border-[#124242] bg-gradient-to-br from-white to-[#124242]/5">
        <div className="flex items-center gap-2 mb-2">
          <FileSpreadsheet size={20} className="text-[#124242]" />
          <h3 className="text-lg font-bold text-stone-800">CSV Recipe Import</h3>
          <span className="badge badge-teal text-xs">Single File</span>
        </div>
        <p className="text-sm text-stone-500 mb-4">
          Import recipes from a single CSV file (one row per ingredient)
        </p>

        <div className="bg-stone-50 rounded-lg p-3 mb-4">
          <p className="text-xs font-semibold text-stone-600 uppercase mb-2">CSV Columns</p>
          <div className="flex flex-wrap gap-2">
            {['recipe_name', 'category', 'visibility_tag', 'ingredient_name', 'quantity', 'unit', 'cooking_steps', 'important_notes'].map(field => (
              <span key={field} className="badge badge-teal text-xs">{field}</span>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <input
            type="file"
            accept=".csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="virsa-input"
            data-testid="import-recipes-simple-file"
          />
          <div className="flex gap-2">
            <button
              onClick={() => handleImport('recipes-simple', file)}
              disabled={!file || importing}
              className="flex-1 btn-primary"
              data-testid="import-recipes-simple-btn"
            >
              {importing ? <span className="spinner" /> : <Upload size={18} />}
              Import CSV
            </button>
            <button
              onClick={() => downloadTemplate('recipes-simple')}
              className="btn-outline"
              title="Download Template with Examples"
            >
              <Download size={18} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ImportCard = ({ type, title, description, fields }) => {
    const [file, setFile] = useState(null);

    return (
      <div className="virsa-card p-6">
        <h3 className="text-lg font-bold text-stone-800 mb-2">{title}</h3>
        <p className="text-sm text-stone-500 mb-4">{description}</p>

        <div className="bg-stone-50 rounded-lg p-3 mb-4">
          <p className="text-xs font-semibold text-stone-600 uppercase mb-2">Required Columns</p>
          <div className="flex flex-wrap gap-2">
            {fields.map(field => (
              <span key={field} className="badge badge-teal text-xs">{field}</span>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <input
            type="file"
            accept=".csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="virsa-input"
            data-testid={`import-${type}-file`}
          />
          <div className="flex gap-2">
            <button
              onClick={() => handleImport(type, file)}
              disabled={!file || importing}
              className="flex-1 btn-primary"
              data-testid={`import-${type}-btn`}
            >
              {importing ? <span className="spinner" /> : <Upload size={18} />}
              Import
            </button>
            <button
              onClick={() => downloadTemplate(type)}
              className="btn-outline"
              title="Download Template"
            >
              <Download size={18} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ExportCard = ({ type, title, description, filename }) => (
    <div className="virsa-card p-6">
      <h3 className="text-lg font-bold text-stone-800 mb-2">{title}</h3>
      <p className="text-sm text-stone-500 mb-4">{description}</p>
      <button
        onClick={() => handleExport(type, filename)}
        className="w-full btn-primary"
        data-testid={`export-${type}-btn`}
      >
        <Download size={18} />
        Export CSV
      </button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="import-export-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-stone-800 tracking-tight">
          Import / Export
        </h1>
        <p className="text-stone-500 mt-1">Manage your data with CSV files</p>
      </div>

      {/* Import Results */}
      {importResults && (
        <div className={`virsa-card p-4 ${importResults.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-center gap-3">
            {importResults.success ? (
              <Check size={20} className="text-green-600" />
            ) : (
              <AlertCircle size={20} className="text-red-600" />
            )}
            <div>
              <span className={importResults.success ? 'text-green-800' : 'text-red-800'}>
                {importResults.message}
              </span>
              {importResults.details?.created !== undefined && (
                <p className="text-sm text-green-600 mt-1">
                  Created: {importResults.details.created} | Updated: {importResults.details.updated}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="import">
        <TabsList className="w-full md:w-auto">
          <TabsTrigger value="import" className="flex-1 md:flex-none">
            <Upload size={16} className="mr-2" />
            Import
          </TabsTrigger>
          <TabsTrigger value="export" className="flex-1 md:flex-none">
            <Download size={16} className="mr-2" />
            Export
          </TabsTrigger>
        </TabsList>

        <TabsContent value="import" className="mt-6">
          {/* Featured Import Options */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <ExcelRecipeImportCard />
            <SimpleRecipeImportCard />
          </div>

          {/* Other Import Options */}
          <h3 className="text-lg font-semibold text-stone-700 mb-4">Other Import Options</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ImportCard
              type="ingredients"
              title="Import Ingredients"
              description="Import your master ingredient list"
              fields={['name', 'default_unit', 'master_sort_order']}
            />
            <ImportCard
              type="recipes"
              title="Import Recipe Metadata"
              description="Import recipe names and categories only"
              fields={['recipe_name', 'category', 'visibility_tag', 'active']}
            />
            <ImportCard
              type="recipe-ingredients"
              title="Import Recipe Ingredients"
              description="Add ingredients to existing recipes"
              fields={['recipe_name', 'ingredient_name', 'qty_per_tiffin', 'unit']}
            />
          </div>

          {/* Import Instructions */}
          <div className="virsa-card p-6 mt-6">
            <h3 className="text-lg font-bold text-stone-800 mb-4 flex items-center gap-2">
              <FileSpreadsheet size={20} className="text-[#124242]" />
              CSV Import Guidelines
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-stone-600">
              <div>
                <h4 className="font-semibold text-stone-800 mb-2">Categories</h4>
                <div className="flex flex-wrap gap-1">
                  {['Daal', 'Sabzi', 'Salad', 'SpecialMain', 'SpecialRoti', 'SpecialRaita', 'SpecialSide', 'Sweet', 'Other'].map(cat => (
                    <span key={cat} className="text-xs bg-stone-100 px-2 py-1 rounded">{cat}</span>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-stone-800 mb-2">Visibility Tags</h4>
                <div className="flex flex-wrap gap-1">
                  {['Regular Rotation Eligible', 'Special Rotation Eligible'].map(tag => (
                    <span key={tag} className="text-xs bg-stone-100 px-2 py-1 rounded">{tag}</span>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-stone-800 mb-2">Quantities</h4>
                <p>Enter quantities for 1 tiffin. Use any unit you prefer (g, kg, ml, tbsp, cup, piece, bunch, etc.)</p>
              </div>
              <div>
                <h4 className="font-semibold text-stone-800 mb-2">File Format</h4>
                <p>CSV format with UTF-8 encoding. First row must be column headers.</p>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="export" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Excel Export - Featured */}
            <div className="virsa-card p-6 border-2 border-[#faa023]">
              <div className="flex items-center gap-2 mb-2">
                <Star size={18} className="text-[#faa023]" fill="#faa023" />
                <h3 className="text-lg font-bold text-stone-800">Export as Excel</h3>
              </div>
              <p className="text-sm text-stone-500 mb-4">
                Download all recipes as Excel file (one sheet per recipe)
              </p>
              <button
                onClick={() => handleExport('recipes-excel', 'recipes.xlsx')}
                className="w-full btn-primary"
                style={{ backgroundColor: '#faa023', borderColor: '#faa023' }}
                data-testid="export-recipes-excel-btn"
              >
                <Download size={18} />
                Export Excel
              </button>
            </div>
            {/* CSV Export */}
            <div className="virsa-card p-6 border-2 border-[#124242]">
              <div className="flex items-center gap-2 mb-2">
                <FileSpreadsheet size={18} className="text-[#124242]" />
                <h3 className="text-lg font-bold text-stone-800">Export as CSV</h3>
              </div>
              <p className="text-sm text-stone-500 mb-4">
                Download complete recipes as single CSV file
              </p>
              <button
                onClick={() => handleExport('recipes-simple', 'recipes_complete.csv')}
                className="w-full btn-primary"
                data-testid="export-recipes-simple-btn"
              >
                <Download size={18} />
                Export CSV
              </button>
            </div>
            <ExportCard
              type="ingredients"
              title="Export Ingredients"
              description="Download your ingredient master list"
              filename="ingredients.csv"
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
